/**
 * Pure display helpers for the Email Campaigns list, extracted from
 * EmailCampaignsPage. Status → MUI Chip color, status → human label, and the
 * open-rate percentage. No money / reseller here — wall-neutral.
 *
 * (The page also keeps an inert Tailwind-class `getStatusColor` inline: it is
 * only there to satisfy a child dialog's legacy `(status) => string` prop and
 * is intentionally NOT part of this module.)
 */
import type { ChipProps } from "@mui/material"

/** Campaign status → MUI Chip palette color (replaces the former Tailwind status badges). */
export function statusChipColor(status: string): ChipProps["color"] {
  switch (status) {
    case "not_sent": return "warning"
    case "scheduled": return "info"
    case "sending": return "warning"
    case "sent": return "success"
    case "paused": return "warning"
    case "failed": return "error"
    default: return "default"
  }
}

/** Campaign status → human-readable label; unknown statuses pass through verbatim. */
export function getStatusLabel(status: string): string {
  switch (status) {
    case "not_sent": return "Not Sent"
    case "scheduled": return "Scheduled"
    case "sending": return "Sending"
    case "sent": return "Sent"
    case "paused": return "Paused"
    case "failed": return "Failed"
    default: return status
  }
}

/** Whole-percent open rate (opened / sent). Guards divide-by-zero → 0. */
export function calculateOpenRate(opened: number, sent: number): number {
  if (sent === 0) return 0
  return Math.round((opened / sent) * 100)
}

/**
 * The toast once crm-api has ACCEPTED a Send Now. Shown only after the
 * request succeeds — it used to say "Handing N emails to Mailjet" before the
 * request even left, so a refused send flashed "Sending" next to its error.
 * `contactsCreated` are the recipients crm-api just saved as CRM contacts
 * (hand-typed addresses, webinar attendees) so the campaign could mail them.
 */
export function sendStartedMessage(recipientCount: number, contactsCreated: string[] = []): string {
  const people = recipientCount === 1 ? "1 person" : `${recipientCount} people`
  let message = `Sending to ${people}. Mailjet takes them in batches of 50 — progress updates as each batch goes. Duplicates and unsubscribed people are left out.`
  if (contactsCreated.length > 0) {
    const shown = contactsCreated.slice(0, 3).join(", ")
    const more = contactsCreated.length > 3 ? ` and ${contactsCreated.length - 3} more` : ""
    const noun = contactsCreated.length === 1 ? "recipient" : "recipients"
    message += ` ${contactsCreated.length} new ${noun} saved to CRM contacts: ${shown}${more}.`
  }
  return message
}
